/**
 * LINKS IN WHAT PEOPLE TYPE, found so they can be clicked.
 *
 * Comments, descriptions and notes are plain text, so a link pasted into one read back as text
 * — to follow it you selected it, copied it and pasted it into the address bar. This finds the
 * links in a piece of text so the page can draw them as links.
 *
 * Only `http`, `https` and bare `www.` addresses. A `javascript:` or `data:` "link" in somebody's
 * comment must stay text, so anything that is not a web address is never made clickable.
 */
export type TextPart = { text: string; href?: string };

const FOUND = /\b(?:https?:\/\/|www\.)[^\s<>"]+/gi;

/**
 * Punctuation that ends a sentence rather than a link: "see https://x.co/a." links to /a, not
 * /a. — and a closing bracket only belongs to the link when the link opened one, as in
 * Wikipedia's "Mercury_(planet)".
 */
function trim(link: string): string {
  let end = link.length;
  while (end > 0) {
    const last = link[end - 1];
    if ('.,;:!?\'"'.includes(last)) {
      end--;
      continue;
    }
    if (last === ')' || last === ']') {
      const open = last === ')' ? '(' : '[';
      const inside = link.slice(0, end);
      if (inside.split(open).length - 1 < inside.split(last).length - 1) {
        end--;
        continue;
      }
    }
    break;
  }
  return link.slice(0, end);
}

export function splitLinks(text: string): TextPart[] {
  const parts: TextPart[] = [];
  let at = 0;
  for (const match of text.matchAll(FOUND)) {
    const link = trim(match[0]);
    if (!link || /^www\.$/i.test(link)) continue;
    const start = match.index ?? 0;
    if (start > at) parts.push({ text: text.slice(at, start) });
    parts.push({ text: link, href: /^www\./i.test(link) ? `https://${link}` : link });
    at = start + link.length;
  }
  if (at < text.length) parts.push({ text: text.slice(at) });
  return parts;
}

/** Just the links, each once, in the order they appear. */
export function findLinks(text: string): string[] {
  return [...new Set(splitLinks(text).flatMap((part) => (part.href ? [part.href] : [])))];
}
